// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Header.css';

const Header = ({ currentUser, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (to) => (e) => {
    if (e) e.preventDefault();
    setMenuOpen(false);
    navigate(to);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Lock body scroll while menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close with Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const toggleMenu = () => setMenuOpen((o) => !o);

  const navItems = [
    {
      to: '/',
      label: 'View schedule',
      index: '01',
      sub: 'Every session, every pitch, week by week.',
    },
    ...(currentUser?.role === 'coach'
      ? [{
          to: '/add-booking',
          label: 'Add booking',
          index: '02',
          sub: 'Reserve a new slot. Coaches only.',
        }]
      : []),
  ];

  const todayLabel = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const initial =
    (currentUser?.name?.[0] || currentUser?.username?.[0] || '·').toUpperCase();

  return (
    <>
      {/* ============ Main header bar ============ */}
      <header className="sw-header">
        <div className="sw-header-inner">
          <Link to="/" className="sw-brand" aria-label="ScheduleWise home">
            <span className="sw-brand-mark">S</span>
            <span className="sw-brand-text">ScheduleWise</span>
          </Link>

          <button
            className="sw-menu-btn"
            onClick={toggleMenu}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="sw-menu-btn-lines" aria-hidden="true">
              <span />
              <span />
            </span>
            <span className="sw-menu-btn-label">Menu</span>
          </button>
        </div>
      </header>

      {/* ============ Full-screen menu overlay ============ */}
      <div
        className={`sw-menu ${menuOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
      >
        <div className="sw-menu-bg" aria-hidden="true" />
        <div className="sw-menu-grain" aria-hidden="true" />
        <svg className="sw-menu-pitch" viewBox="0 0 120 180" aria-hidden="true">
          <rect x="8" y="12" width="104" height="156" rx="2" />
          <line x1="8" y1="90" x2="112" y2="90" />
          <circle cx="60" cy="90" r="1.4" />
          <line x1="8" y1="26" x2="112" y2="26" />
          <line x1="8" y1="34" x2="112" y2="34" />
          <line x1="8" y1="60" x2="112" y2="60" />
          <line x1="8" y1="120" x2="112" y2="120" />
          <line x1="8" y1="146" x2="112" y2="146" />
          <line x1="8" y1="154" x2="112" y2="154" />
          <rect x="53" y="12" width="14" height="5" />
          <rect x="53" y="163" width="14" height="5" />
          <path d="M 52 34 A 8 8 0 0 1 68 34" />
          <path d="M 52 146 A 8 8 0 0 0 68 146" />
        </svg>

        <div className="sw-menu-inner">
          {/* Top bar inside the menu */}
          <div className="sw-menu-top">
            <a href="/" className="sw-brand sw-brand--light" onClick={goTo('/')}>
              <span className="sw-brand-mark sw-brand-mark--light">S</span>
              <span className="sw-brand-text sw-brand-text--light">ScheduleWise</span>
            </a>

            <div className="sw-menu-top-right">
              <div className="sw-menu-date">
                <span className="sw-menu-date-label">Today</span>
                <span className="sw-menu-date-value">{todayLabel}</span>
              </div>
              <button
                className="sw-close-btn"
                onClick={toggleMenu}
                aria-label="Close menu"
              >
                <span>Close</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M18 6L6 18" /><path d="M6 6L18 18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Eyebrow */}
          <div className="sw-menu-eyebrow">
            <span className="sw-menu-dot" />
            <span>Navigation</span>
            <span className="sw-menu-tick-row" aria-hidden="true">
              <i /><i /><i /><i /><i /><i /><i /><i />
            </span>
          </div>

          {/* Big nav items */}
          <nav className="sw-menu-nav" aria-label="Main">
            {navItems.map(({ to, label, sub, index }, i) => {
              const isActive = location.pathname === to;
              return (
                <a
                  key={to}
                  href={to}
                  className={`sw-menu-link ${isActive ? 'is-active' : ''}`}
                  style={{ '--i': i }}
                  onClick={goTo(to)}
                >
                  <div className="sw-menu-link-top">
                    <span className="sw-menu-link-index">{index}</span>
                    <span className="sw-menu-link-status">
                      {isActive ? 'Current' : 'Jump to'}
                    </span>
                  </div>
                  <div className="sw-menu-link-main">
                    <span className="sw-menu-link-label">{label}</span>
                    {sub && <span className="sw-menu-link-sub">{sub}</span>}
                  </div>
                  <div className="sw-menu-link-foot">
                    <span className="sw-menu-link-rule" aria-hidden="true" />
                    <span className="sw-menu-link-arrow" aria-hidden="true">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
                      </svg>
                    </span>
                  </div>
                </a>
              );
            })}
          </nav>

          {/* Editorial motto strip */}
          <div className="sw-menu-motto">
            <span className="sw-menu-motto-rule" aria-hidden="true" />
            <p>
              <em>Schedule the game.</em> Win the week.
            </p>
            <span className="sw-menu-motto-rule" aria-hidden="true" />
          </div>

          {/* Footer user card */}
          <div className="sw-menu-foot">
            {currentUser && (
              <div className="sw-user-card">
                <div className="sw-user-avatar" aria-hidden="true">{initial}</div>
                <div className="sw-user-meta">
                  <div className="sw-user-eyebrow">Signed in as</div>
                  <div className="sw-user-name">
                    {currentUser.name || currentUser.username}
                  </div>
                  {currentUser.role && (
                    <div className="sw-user-role">
                      <em>{currentUser.role}</em>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button className="sw-logout" onClick={onLogout}>
              <span>Sign out</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>

          <div className="sw-menu-corner sw-menu-corner--tl" aria-hidden="true" />
          <div className="sw-menu-corner sw-menu-corner--br" aria-hidden="true" />
        </div>
      </div>
    </>
  );
};

export default Header;
